import { Series } from '@aero-platform/shared'
import { useMsal } from '@azure/msal-react'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Sidebar } from 'primereact/sidebar'
import { Dialog } from 'primereact/dialog'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useGetRaceTeams } from '../api/raceteam-API'
import { useGetUserLazy } from '../api/user-API'
import { useGetSystemMessages } from '../api/system-message-API'
import { ReactComponent as LogoIcon } from '../assets/svg/gm-logo.svg'
import { ReactComponent as HamburgerIcon } from '../assets/svg/hamburger.svg'
import { MessageServicePromiseClient } from '../grpc/Notification_grpc_web_pb'
import { MessageRequest } from '../grpc/Notification_pb'
import { protoService } from '../proto/ProtoService'
import simUploadQueueStore, { PendingSimUpload } from '../services/simUploadQueueStore'
import { RaceTeam, SelectOption } from '../types/types'
import { queryString2JsonString } from '../types/util'
import { AccessTokenKey, CurrentUserKey, GMMSRaceTeamKey } from '../utils/constants'
import { appEventInstance } from '../utils/event'
import { isDashboardOnlyRole } from '../utils/roles'
import {
  currentTeamState,
  currentUserState,
  isF1TeamState,
  teamListState,
  themeState
} from '../utils/state-atoms'
import { DropdownStyle } from '../utils/styling-constants'
import IconButton from './buttons/IconButton'
import { BellIcon } from './Icons'
import NotificationLine, { getMessageStatus } from './NotificationLine'
import DOMPurify from 'dompurify'

const savedTeam = localStorage.getItem(GMMSRaceTeamKey)
const client = new MessageServicePromiseClient(
  process.env.REACT_APP_PLATFORM_STREAMER_URL || 'https://adp-platform-streamer.gm-motorsports.com/'
)

// LocalStorage key for viewed system messages
const VIEWED_MESSAGES_KEY = 'viewedSystemMessages'

// Helper functions for managing viewed messages
const getViewedMessages = (): string[] => {
  try {
    const stored = localStorage.getItem(VIEWED_MESSAGES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (err) {
    console.error('Error reading viewed messages:', err)
    return []
  }
}

const markMessageAsViewed = (messageId: string) => {
  try {
    const viewed = getViewedMessages()
    if (!viewed.includes(messageId)) {
      viewed.push(messageId)
      localStorage.setItem(VIEWED_MESSAGES_KEY, JSON.stringify(viewed))
    }
  } catch (err) {
    console.error('Error marking message as viewed:', err)
  }
}

const isMessageViewed = (messageId: string): boolean => {
  return getViewedMessages().includes(messageId)
}

export default function Navbar() {
  const navigate = useNavigate()
  const [theme, setTheme] = useRecoilState(themeState)
  const [showSidebar, setShowSidebar] = useState(false)
  const { instance: msalInstance } = useMsal()
  const currentUser = useRecoilValue(currentUserState)
  const isDashboardOnly = isDashboardOnlyRole(currentUser?.role?.roleId)
  const setF1Team = useSetRecoilState(isF1TeamState)
  const [selectedTeam, setSelectedTeam] = useState(savedTeam || '')
  const currentTeam = useRecoilValue(currentTeamState)
  const [teamList, setTeamList] = useState<SelectOption[]>([])
  const teams = useRecoilValue(teamListState)
  const [, fetchRaceTeams] = useGetRaceTeams()
  const [{ loading: loadingUser }, fetchUser] = useGetUserLazy()
  const refTeam = useRef<string>(savedTeam)

  const refNotification = useRef(null)

  const [notifications, setNotifications] = useState([])
  const [pendingSimUploads, setPendingSimUploads] = useState<PendingSimUpload[]>(
    simUploadQueueStore.getAll()
  )
  const [systemMessages, setSystemMessages] = useState<any[]>([])
  const [selectedMessage, setSelectedMessage] = useState<any | null>(null)
  const [showMessageDialog, setShowMessageDialog] = useState(false)

  const [{ data: systemMessagesData }, fetchSystemMessages] = useGetSystemMessages()

  useEffect(() => {
    return simUploadQueueStore.subscribe(setPendingSimUploads)
  }, [])

  useEffect(() => {
    setSelectedTeam(currentTeam)
    refTeam.current = currentTeam
  }, [currentTeam])

  const getNotifications = () => {
    var request = new MessageRequest()
    request.setTopic('ALL')
    request.setMessagecount(10)
    request.setSendduration(10)
    var stream = client.getMessages(request)
    // Start listening on the data event, this is the event that is used to notify that new data arrives
    stream.on('data', function (response) {
      // Convert Response to Object
      const newData = response.toObject()
      console.log('-------------------GRPC Data received:', newData)
      const notifications = newData.valuesList
        .map((item: any) => {
          const command = item.platformremotecommand
          const notification = {
            status: getMessageStatus(item),
            eventType: item.eventtype,
            timestamp: item.timestamp,
            message: command.resulttext,
            gmref: command.gmref
          }
          return notification
        })
        .filter(item => {
          return item.gmref && item.gmref.raceteamid === refTeam.current
        })
      setNotifications(oldNotifications => {
        return notifications
      })
    })
  }

  useEffect(() => {
    getNotifications()
    fetchSystemMessages()
  }, [])

  // Process and filter system messages
  useEffect(() => {
    if (systemMessagesData && currentUser && currentTeam) {
      try {
        const decoded = protoService.decodeProtoResponse(systemMessagesData, 'systemmessage')
        if (Array.isArray(decoded)) {
          // Calculate 60 days ago timestamp
          const sixtyDaysAgo = new Date()
          sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
          const sixtyDaysAgoTime = sixtyDaysAgo.getTime()

          // Filter messages based on user role, team, date, and exclusion list
          const filtered = decoded.filter((message: any) => {
            // Check if user's role is in the recipients list
            const roleMatch =
              message.roleRecipients && message.roleRecipients.includes(currentUser.role?.roleId)

            // Check if user's team is in the recipients list
            const teamMatch =
              message.seriesRecipients && message.seriesRecipients.includes(currentTeam)

            // Check if message is within last 60 days
            const messageDate = new Date(message.timeCreated).getTime()
            const dateMatch = messageDate >= sixtyDaysAgoTime

            // Check if user is NOT in the excluded recipients list
            const userEmail = currentUser?.email || ''
            const userName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim()
            const userDisplay =
              userEmail && userName ? `${userName} (${userEmail})` : userEmail || userName
            const excludedRecipients: string[] = message.excludedRecipients || []
            const excludedByEmailInDisplay =
              Boolean(userEmail) &&
              excludedRecipients.some(excluded => excluded.endsWith(`(${userEmail})`))
            const notExcluded = !userEmail
              ? true
              : !excludedRecipients.includes(userEmail) &&
                !excludedRecipients.includes(userDisplay) &&
                !excludedByEmailInDisplay

            // Message must match role AND team AND be within 60 days AND user not excluded
            return roleMatch && teamMatch && dateMatch && notExcluded
          })

          // Sort by timeCreated descending (newest first)
          const sorted = filtered.sort((a, b) => {
            const dateA = new Date(a.timeCreated).getTime()
            const dateB = new Date(b.timeCreated).getTime()
            return dateB - dateA
          })

          setSystemMessages(sorted)
        }
      } catch (err) {
        console.error('Error decoding system messages:', err)
      }
    }
  }, [systemMessagesData, currentUser, currentTeam])

  useEffect(() => {
    setTeamList(
      teams.map(item => {
        return {
          label: item,
          value: item
        }
      })
    )
  }, [teams])

  useEffect(() => {
    if (selectedTeam) {
      const query = queryString2JsonString(`raceTeamId=${selectedTeam}`)
      fetchRaceTeams(`query=${query}`).then(res => {
        if (res.data) {
          const tmpList = res.data.protoBytesList.map((item: string) => {
            return protoService.deSerialize(item, 'raceteam')
          })
          if (tmpList[0]) {
            setF1Team((tmpList[0] as RaceTeam).series === Series.SERIES_F1)
          } else {
            setF1Team(false)
          }
        }
      })
    } else {
      setF1Team(false)
    }
  }, [selectedTeam])

  const handleSignOut = () => {
    msalInstance.logoutRedirect().catch(error => console.error(error))
  }

  return (
    <div className="flex flex-col items-stretch w-full !pt-0 px-0">
      <div className="flex items-center h-16 mx-6">
        <div className="flex items-center !w-auto space-x-6">
          {!isDashboardOnly && (
            <label tabIndex={0} className="outline-none">
              <HamburgerIcon
                className="w-[30px] h-[25px] fill-gm-blue dark:fill-light-blue"
                role="button"
                onClick={() => setShowSidebar(true)}
              />
            </label>
          )}
          <label tabIndex={0} className="outline-none">
            <LogoIcon
              className="w-8 h-8 fill-gm-blue dark:fill-light-blue"
              role="button"
              onClick={() => navigate('/')}
            />
          </label>
          <div className="text-[1.2em] text-gm-blue dark:text-light-blue uppercase">
            Aero Design
          </div>
        </div>
        {currentUser && (
          <div className="items-center ml-2 flex grow justify-end">
            <div className="pr-2 dark:text-neutral">Team: </div>
            <Dropdown
              value={selectedTeam}
              loading={loadingUser}
              pt={{
                ...DropdownStyle,
                root: {
                  ...DropdownStyle.root,
                  className:
                    'w-48 !bg-transparent text-white dark:border-neutral-10 dark:text-neutral-10'
                }
              }}
              placeholder="Select a team"
              onChange={async e => {
                const user = JSON.parse(localStorage.getItem(CurrentUserKey))

                // Try fetching by OID first
                let userData = await fetchUser(
                  'query=' +
                    queryString2JsonString(
                      `cloudProviderLDAPRef=${user.cloudProviderLDAPRef}&raceTeamId=${e.value}`
                    )
                ).catch(() => ({ data: null }))

                // If not found by OID, try by email (for records not yet migrated)
                if (!userData.data || !protoService.decodeProtoResponse(userData.data, 'user')[0]) {
                  userData = await fetchUser(
                    'query=' + queryString2JsonString(`email=${user.email}&raceTeamId=${e.value}`)
                  ).catch(() => ({ data: null }))
                }

                if (userData.data) {
                  const fetchedUser = protoService.decodeProtoResponse(userData.data, 'user')
                  if (!fetchedUser[0]) {
                    toast.error(
                      `Cannot switch to race team ${e.value}: your account hasn't been set up, please contact the CFD team.`
                    )
                    return
                  }

                  // Migrate if needed (user found by email but doesn't have OID)
                  if (fetchedUser[0].cloudProviderLDAPRef !== user.cloudProviderLDAPRef) {
                    try {
                      await fetch(
                        `${process.env.REACT_APP_API_BASE_URL}/Users/${fetchedUser[0]._id}`,
                        {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem(AccessTokenKey)}`,
                            gmmsraceteamid: e.value
                          },
                          body: JSON.stringify({
                            firstName: fetchedUser[0].firstName,
                            lastName: fetchedUser[0].lastName,
                            email: fetchedUser[0].email,
                            raceTeamId: fetchedUser[0].raceTeamId,
                            role: {
                              roleId: fetchedUser[0].role?.roleId || 'read-only',
                              name: fetchedUser[0].role?.name || 'Read-Only'
                            },
                            rescaleTokens: fetchedUser[0].rescaleTokens || [],
                            cloudProviderLDAPRef: user.cloudProviderLDAPRef,
                            updatedByUser: user.email
                          })
                        }
                      )
                      fetchedUser[0].cloudProviderLDAPRef = user.cloudProviderLDAPRef
                    } catch (err) {
                      console.error('Failed to migrate team record:', err)
                    }
                  }

                  if (
                    !fetchedUser[0].rescaleTokens ||
                    !fetchedUser[0].rescaleTokens[0] ||
                    !fetchedUser[0].rescaleTokens[0].token
                  ) {
                    // Just log it - don't block the team switch
                    console.log(`Note: No rescale token configured for team ${e.value}`)
                  }
                  // switch team if everything is good
                  localStorage.setItem(GMMSRaceTeamKey, e.value)
                  setNotifications([])
                  refTeam.current = e.value
                  setSelectedTeam(e.value)
                  appEventInstance.teamChanged()
                } else {
                  toast.error(
                    'Error loading user data, please contact the CFD team if this keeps happening.'
                  )
                }
              }}
              options={teamList}
            />
            <div className="w-10"></div>
            {/* Bell icon with badge for pending sim uploads */}
            <div className="relative">
              <IconButton
                clear
                icon={<BellIcon className="w-6 h-6 fill-gm-blue " />}
                onClick={e => {
                  refNotification.current && refNotification.current.toggle(e)
                }}
              />
              {pendingSimUploads.filter(
                u => u.status === 'done' || u.status === 'failed' || u.status === 'cancelled'
              ).length +
                systemMessages.filter(msg => !isMessageViewed(msg._id)).length >
                0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold pointer-events-none">
                  {pendingSimUploads.filter(
                    u => u.status === 'done' || u.status === 'failed' || u.status === 'cancelled'
                  ).length + systemMessages.filter(msg => !isMessageViewed(msg._id)).length}
                </span>
              )}
            </div>
            <OverlayPanel ref={refNotification} className="w-[600px]" dismissable>
              <div className="flex flex-col items-stretch w-full space-y-3">
                {/* Completed sim file uploads - show terminal states only */}
                {pendingSimUploads.filter(
                  u => u.status === 'done' || u.status === 'failed' || u.status === 'cancelled'
                ).length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gm-blue dark:text-gm-aqua-light">
                      Background Sim Uploads
                    </span>
                    {pendingSimUploads
                      .filter(
                        u =>
                          u.status === 'done' || u.status === 'failed' || u.status === 'cancelled'
                      )
                      .map(u => {
                        const isSuccess = u.status === 'done'
                        const isFailed = u.status === 'failed'
                        const isCancelled = u.status === 'cancelled'

                        return (
                          <div
                            key={u.runId}
                            className={`flex items-center gap-2 text-sm p-3 rounded-md border transition-colors ${
                              isSuccess
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 cursor-pointer'
                                : isFailed
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
                            }`}
                            onClick={
                              isSuccess
                                ? () => {
                                    navigate(`/submissions/${u.runId}`)
                                    // @ts-ignore
                                    refNotification.current?.hide()
                                  }
                                : undefined
                            }>
                            <span
                              className={`w-3 h-3 rounded-full flex-shrink-0 ${
                                isSuccess ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-gray-500'
                              }`}
                            />
                            <div className="flex flex-col min-w-0 flex-1">
                              <span
                                className={`font-medium truncate ${
                                  isSuccess
                                    ? 'text-green-800 dark:text-green-200'
                                    : isFailed
                                    ? 'text-red-800 dark:text-red-200'
                                    : 'text-gray-800 dark:text-gray-200'
                                }`}>
                                <strong>{u.fileName}</strong>{' '}
                                {isSuccess
                                  ? 'uploaded successfully'
                                  : isFailed
                                  ? 'upload failed'
                                  : 'upload cancelled'}
                              </span>
                              <span
                                className={`text-xs truncate ${
                                  isSuccess
                                    ? 'text-green-700 dark:text-green-300'
                                    : isFailed
                                    ? 'text-red-700 dark:text-red-300'
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                {u.runName} {isSuccess && '— Click to complete your submission'}
                              </span>
                            </div>
                            <button
                              className={`ml-auto text-xs ${
                                isSuccess
                                  ? 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200'
                                  : isFailed
                                  ? 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200'
                                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                              }`}
                              onClick={e => {
                                e.stopPropagation()
                                simUploadQueueStore.dismiss(u.runId)
                              }}>
                              ✕
                            </button>
                          </div>
                        )
                      })}
                    {notifications.length > 0 && (
                      <hr className="border-gray-200 dark:border-neutral-70" />
                    )}
                  </div>
                )}
                {notifications.map(item => (
                  <NotificationLine notification={item} key={item.timestamp} />
                ))}
                {/* System Messages Section */}
                {systemMessages.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {(notifications.length > 0 ||
                      pendingSimUploads.filter(
                        u =>
                          u.status === 'done' || u.status === 'failed' || u.status === 'cancelled'
                      ).length > 0) && <hr className="border-gray-200 dark:border-neutral-70" />}
                    <span className="text-xs font-semibold uppercase tracking-wide text-gm-blue dark:text-gm-aqua-light">
                      System Announcements
                    </span>
                    {systemMessages.map((message, index) => {
                      const messageDate = new Date(message.timeCreated)
                      const formattedDate = messageDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })

                      return (
                        <div
                          key={message._id || index}
                          className={`flex items-start gap-2 text-sm p-3 rounded-md border cursor-pointer transition-colors ${
                            isMessageViewed(message._id)
                              ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20 hover:bg-gray-100 dark:hover:bg-gray-900/30'
                              : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          }`}
                          onClick={() => {
                            markMessageAsViewed(message._id)
                            setSelectedMessage(message)
                            setShowMessageDialog(true)
                            // Re-trigger state update to reflect viewed status
                            setSystemMessages([...systemMessages])
                          }}>
                          <span
                            className={`w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ${
                              isMessageViewed(message._id) ? 'bg-gray-400' : 'bg-blue-500'
                            }`}
                          />
                          <div className="flex flex-col min-w-0 flex-1">
                            <span
                              className={`font-semibold ${
                                isMessageViewed(message._id)
                                  ? 'text-gray-700 dark:text-gray-300'
                                  : 'text-blue-800 dark:text-blue-200'
                              }`}>
                              {message.subject}
                            </span>
                            <span
                              className={`text-xs ${
                                isMessageViewed(message._id)
                                  ? 'text-gray-600 dark:text-gray-400'
                                  : 'text-blue-700 dark:text-blue-300'
                              }`}>
                              {formattedDate}
                            </span>
                          </div>
                          <span
                            className={`text-xs flex-shrink-0 ${
                              isMessageViewed(message._id)
                                ? 'text-gray-500 dark:text-gray-400'
                                : 'text-blue-600 dark:text-blue-400'
                            }`}>
                            →
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </OverlayPanel>
            {/* System Message Dialog */}
            <Dialog
              header={selectedMessage?.subject || 'System Message'}
              visible={showMessageDialog}
              onHide={() => {
                setShowMessageDialog(false)
                setSelectedMessage(null)
              }}
              style={{ width: '50vw' }}
              breakpoints={{ '960px': '75vw', '641px': '90vw' }}>
              {selectedMessage && (
                <div className="space-y-4">
                  <div className="flex flex-col">
                    <label className="mb-2 font-medium text-gray-700 dark:text-gray-300 text-sm">
                      Message
                    </label>
                    <div
                      className="p-3 bg-gray-50 dark:bg-neutral-80 rounded"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(selectedMessage.content || '')
                      }}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="mb-2 font-medium text-gray-700 dark:text-gray-300 text-sm">
                      Date Sent
                    </label>
                    <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded text-sm">
                      {new Date(selectedMessage.timeCreated).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  {selectedMessage.messageType && (
                    <div className="flex flex-col">
                      <label className="mb-2 font-medium text-gray-700 dark:text-gray-300 text-sm">
                        Type
                      </label>
                      <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded text-sm capitalize">
                        {selectedMessage.messageType}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Dialog>
          </div>
        )}
      </div>
      <Sidebar
        visible={showSidebar}
        onHide={() => setShowSidebar(false)}
        pt={{
          root: {
            className: 'min-w-fit !border-0'
          },
          closeButtonIcon: {
            className: 'border-0 hover:border-0'
          },
          closeIcon: {
            className: 'text-white hover:text-dark-blue dark:text-white '
          }
        }}
        style={{ paddingLeft: 0 }}>
        <ul className="px-0 w-80 min-h-full text-lg text-neutral-10">
          <li className="flex items-center pb-10">
            <label tabIndex={0} className="outline-none">
              <LogoIcon
                className="w-8 h-8 fill-white dark:fill-light-blue"
                role="button"
                onClick={() => navigate('/')}
              />
            </label>
            <div className="font-semibold text-white dark:text-light-blue uppercase ml-2">
              Aero Design
            </div>
          </li>
          <li>
            <label>
              <Link
                to="/dashboard"
                className="hover:text-white outline-none"
                onClick={() => setShowSidebar(false)}>
                Dashboard
              </Link>
            </label>
          </li>
          <li>
            <label>
              <Link
                to="/vehicles"
                className="hover:text-white outline-none"
                onClick={() => setShowSidebar(false)}>
                Cars
              </Link>
            </label>
          </li>
          <li className="mt-2">
            <label>Submissions</label>
            <ul className="ml-4">
              <li>
                <Link
                  to="/submissions"
                  className="hover:text-white outline-none"
                  onClick={() => setShowSidebar(false)}>
                  List
                </Link>
              </li>
              <li>
                <Link
                  to="/external-case"
                  className="hover:text-white outline-none"
                  onClick={() => setShowSidebar(false)}>
                  Enter External Case
                </Link>
              </li>
            </ul>
          </li>
          <li className="mt-2">
            <label>
              <Link
                to="/rescale-queue"
                className="hover:text-white outline-none"
                onClick={() => setShowSidebar(false)}>
                Task Queue
              </Link>
            </label>
          </li>
          <li className="mt-10 flex flex-col">
            <div className="text-gray-200">AERO GLOBAL SETTINGS</div>
            <hr className="my-2 gm-background h-[2px] border-0" />
            <label className="mt-1">
              <Link
                to="/methodologies"
                className="hover:text-white outline-none mt-4 cursor-pointer"
                onClick={() => setShowSidebar(false)}>
                Methodology Sets
              </Link>
            </label>
            <label htmlFor="main-drawer" className="mt-2">
              <Link
                to="/attitude-sets"
                className="hover:text-white outline-none cursor-pointer"
                onClick={() => setShowSidebar(false)}>
                Attitude Sets
              </Link>
            </label>

            <label htmlFor="main-drawer" className="mt-2">
              <Link
                to="/pressure-tap-import"
                className="hover:text-white outline-none cursor-pointer"
                onClick={() => setShowSidebar(false)}>
                Pressure Tap Sets
              </Link>
            </label>
            <label htmlFor="main-drawer" className="mt-2">
              <Link
                to="/workflow-templates"
                className="hover:text-white outline-none cursor-pointer"
                onClick={() => setShowSidebar(false)}>
                Workflow Templates
              </Link>
            </label>
            <label htmlFor="main-drawer" className="mt-2 hidden">
              <Link
                to="/project-configs"
                className="hover:text-white outline-none cursor-pointer"
                onClick={() => setShowSidebar(false)}>
                Project Config
              </Link>
            </label>
            <label htmlFor="main-drawer" className="mt-2 hidden">
              <Link
                to="/workflows"
                className="hover:text-white outline-none cursor-pointer"
                onClick={() => setShowSidebar(false)}>
                Workflows
              </Link>
            </label>
          </li>
          <li className="mt-10 flex flex-col">
            <div className="text-gray-200">PRODUCTS</div>
            <hr className="my-2 gm-background h-[2px] border-0" />
            <label className="mt-1">
              <Link
                to="/mav-aero"
                className="hover:text-white outline-none mt-4 cursor-pointer"
                onClick={() => setShowSidebar(false)}>
                MAV Aero
              </Link>
            </label>
          </li>
          <li className="mt-10 flex flex-col">
            <div className="text-gray-200">HELP</div>
            <hr className="my-2 gm-background h-[2px] border-0" />
            <label className="mt-1">
              <Link
                to="/platform-docs"
                className="hover:text-white outline-none mt-4 cursor-pointer"
                onClick={() => setShowSidebar(false)}>
                Platform Documentation
              </Link>
            </label>
          </li>
          {(currentUser?.role?.roleId === 'admin' || currentUser?.role?.roleId === 'developer') && (
            <li className="mt-10 flex flex-col">
              <div className="text-gray-200">ADMIN</div>
              <hr className="my-2 gm-background h-[2px] border-0" />
              <label className="mt-1">
                <Link
                  to="/admin-task-management"
                  className="hover:text-white outline-none mt-4 cursor-pointer"
                  onClick={() => setShowSidebar(false)}>
                  Task Management
                </Link>
              </label>
              <label htmlFor="main-drawer" className="mt-2">
                <Link
                  to="/users"
                  className="hover:text-white outline-none cursor-pointer"
                  onClick={() => setShowSidebar(false)}>
                  User Management
                </Link>
              </label>
            </li>
          )}
          <li className="mt-10 flex flex-col space-y-2">
            <div className="text-gray-200">SETTINGS</div>
            <hr className="my-2 gm-background h-[2px] border-0" />
            <div className="flex items-center">
              <InputSwitch
                checked={theme === 'dark'}
                onChange={e => {
                  const newTheme = e.value ? 'dark' : 'light'
                  setTheme(newTheme)
                  localStorage.setItem('theme', newTheme)
                  if (newTheme === 'dark') {
                    document.body.classList.add('dark')
                  } else {
                    document.body.classList.remove('dark')
                  }
                }}
                pt={{
                  root: {
                    className: '!h-[24px] w-12'
                  },
                  slider: {
                    className: 'h-[24px] !w-12 dark:!bg-neutral-10'
                  }
                }}
              />
              <label className="ml-2">Dark Mode</label>
            </div>

            <label>
              <button
                className="hover:text-white outline-none mt-4 cursor-pointer"
                onClick={handleSignOut}>
                Sign Out
              </button>
            </label>
          </li>
        </ul>
      </Sidebar>
      <hr className="gm-background h-[2px] border-0" />
    </div>
  )
}
